import { useEffect, useRef } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  ReactFlowProvider,
  useReactFlow,
  type Connection,
  type Edge,
  type EdgeTypes,
  type Node,
  type NodeTypes,
  type OnEdgesChange,
  type OnNodesChange,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Box } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useAppCustomStyles } from "../../../assets/theme/custom/hooks";
import type { PrepFlowNodeData } from "../utils/graphAdapter";
import PrepDesignStatusBar from "./PrepDesignStatusBar";
import PrepCorrelationEdge from "./edges/PrepCorrelationEdge";
import PrepDslQueryNode from "./nodes/PrepDslQueryNode";
import PrepAdaptorQueryNode from "./nodes/PrepAdaptorQueryNode";
import PrepOpenApiQueryNode from "./nodes/PrepOpenApiQueryNode";
import PrepOutputNode from "./nodes/PrepOutputNode";

const nodeTypes = {
  prepDslQuery: PrepDslQueryNode,
  prepAdaptorQuery: PrepAdaptorQueryNode,
  prepOpenApiQuery: PrepOpenApiQueryNode,
  prepOutput: PrepOutputNode,
} as NodeTypes;
const edgeTypes = { prepCorrelation: PrepCorrelationEdge } as EdgeTypes;

type CanvasProps = {
  nodes: Node<PrepFlowNodeData>[];
  edges: Edge[];
  onNodesChange: OnNodesChange<Node<PrepFlowNodeData>>;
  onEdgesChange: OnEdgesChange<Edge>;
  onConnect: (connection: Connection) => void;
  onNodeClick: (_: unknown, node: Node<PrepFlowNodeData>) => void;
  onEdgeClick: (_: unknown, edge: Edge) => void;
  nodeCount: number;
  edgeCount: number;
  staleCount: number;
  incompleteEdgeCount: number;
  validationErrors: string[];
  lastValidatedAt: string | null;
};

function FlowCanvasInner({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onConnect,
  onNodeClick,
  onEdgeClick,
  nodeCount,
  edgeCount,
  staleCount,
  incompleteEdgeCount,
  validationErrors,
  lastValidatedAt,
}: CanvasProps) {
  const custom = useAppCustomStyles();
  const theme = useTheme();
  const { fitView } = useReactFlow();
  const prevCount = useRef(nodes.length);

  useEffect(() => {
    if (nodes.length !== prevCount.current) {
      prevCount.current = nodes.length;
      requestAnimationFrame(() => {
        fitView({ padding: 0.2, duration: 200 }).catch(() => undefined);
      });
    }
  }, [nodes.length, fitView]);

  return (
    <Box sx={custom.components.dataPrep.flowCanvasOuter}>
      <Box sx={custom.components.dataPrep.flowCanvasInner}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          onEdgeClick={onEdgeClick}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          deleteKeyCode={["Backspace", "Delete"]}
          fitView
          style={custom.components.dataPrep.flowCanvasStyle}
        >
          <Background />
          <Controls showInteractive={false} />
          <MiniMap
            nodeColor={() => theme.palette.primary.main}
            nodeStrokeColor={() => theme.palette.divider}
            maskColor={alpha(
              theme.palette.background.default,
              theme.palette.mode === "dark" ? 0.72 : 0.08
            )}
            style={{ backgroundColor: theme.palette.background.paper }}
          />
        </ReactFlow>
      </Box>
      <PrepDesignStatusBar
        nodeCount={nodeCount}
        edgeCount={edgeCount}
        staleCount={staleCount}
        incompleteEdgeCount={incompleteEdgeCount}
        validationErrors={validationErrors}
        lastValidatedAt={lastValidatedAt}
      />
    </Box>
  );
}

export default function PrepFlowCanvas(props: CanvasProps) {
  return (
    <ReactFlowProvider>
      <FlowCanvasInner {...props} />
    </ReactFlowProvider>
  );
}
